import { useState } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";

const AddUseCaseModal = ({ filterOptions, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: "",
    summary: "",
    industry: [],
    product: [],
    useCase: [],
    market: [],
    tags: [],
    clientNote: "",
    painPoints: [""],
    solution: "",
    valueProposition: [""],
    technicalContext: ""
  });

  const [newTag, setNewTag] = useState("");
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleMultiSelectToggle = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const handleArrayFieldAdd = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], ""]
    }));
  };

  const handleArrayFieldChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const handleArrayFieldRemove = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag("");
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.summary.trim()) newErrors.summary = "Summary is required";
    if (formData.product.length === 0) newErrors.product = "At least one product is required";
    if (!formData.clientNote.trim()) newErrors.clientNote = "Client name is required";
    if (!formData.solution.trim()) newErrors.solution = "Solution is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const cleanedData = {
        ...formData,
        painPoints: formData.painPoints.filter(p => p.trim()),
        valueProposition: formData.valueProposition.filter(v => v.trim())
      };
      onSubmit(cleanedData);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 modal-backdrop"
      onClick={handleBackdropClick}
      data-testid="add-usecase-modal"
    >
      <div className="relative w-full max-w-3xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-slate-900 font-['Manrope']">
              Add New Use Case
            </h2>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors"
              data-testid="add-modal-close-btn"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <ScrollArea className="h-[calc(90vh-160px)]">
            <div className="p-6 space-y-6">
              {/* Title */}
              <div>
                <Label htmlFor="title" className="text-sm font-semibold text-slate-700">
                  Title <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                  placeholder="Short descriptive title (no company name)"
                  className={`mt-1.5 ${errors.title ? "border-red-500" : ""}`}
                  data-testid="add-title-input"
                />
                {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
              </div>

              {/* Summary */}
              <div>
                <Label htmlFor="summary" className="text-sm font-semibold text-slate-700">
                  Summary <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="summary"
                  value={formData.summary}
                  onChange={(e) => handleInputChange("summary", e.target.value)}
                  placeholder="2-3 sentence plain-English summary of the problem and solution"
                  className={`mt-1.5 min-h-24 ${errors.summary ? "border-red-500" : ""}`}
                  data-testid="add-summary-input"
                />
                {errors.summary && <p className="text-red-500 text-xs mt-1">{errors.summary}</p>}
              </div>

              {/* Client Note */}
              <div>
                <Label htmlFor="clientNote" className="text-sm font-semibold text-slate-700">
                  Client Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="clientNote"
                  value={formData.clientNote}
                  onChange={(e) => handleInputChange("clientNote", e.target.value)}
                  placeholder="Name of the company this was built for"
                  className={`mt-1.5 ${errors.clientNote ? "border-red-500" : ""}`}
                  data-testid="add-client-input"
                />
                {errors.clientNote && <p className="text-red-500 text-xs mt-1">{errors.clientNote}</p>}
              </div>

              {/* Product Selection */}
              <div>
                <Label className="text-sm font-semibold text-slate-700">
                  Products <span className="text-red-500">*</span>
                </Label>
                <div className="flex flex-wrap gap-3 mt-2">
                  {filterOptions.products.map((product) => (
                    <label key={product} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.product.includes(product)}
                        onCheckedChange={() => handleMultiSelectToggle("product", product)}
                        className="border-slate-300 data-[state=checked]:bg-[#FF6F40] data-[state=checked]:border-[#FF6F40]"
                      />
                      <span className="text-sm text-slate-600">{product}</span>
                    </label>
                  ))}
                </div>
                {errors.product && <p className="text-red-500 text-xs mt-1">{errors.product}</p>}
              </div>

              {/* Industry Selection */}
              <div>
                <Label className="text-sm font-semibold text-slate-700">Industries</Label>
                <div className="flex flex-wrap gap-3 mt-2">
                  {filterOptions.industries.map((industry) => (
                    <label key={industry} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.industry.includes(industry)}
                        onCheckedChange={() => handleMultiSelectToggle("industry", industry)}
                        className="border-slate-300 data-[state=checked]:bg-[#FF6F40] data-[state=checked]:border-[#FF6F40]"
                      />
                      <span className="text-sm text-slate-600">{industry}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Use Case Type Selection */}
              <div>
                <Label className="text-sm font-semibold text-slate-700">Use Case Types</Label>
                <div className="flex flex-wrap gap-3 mt-2">
                  {filterOptions.useCases.map((type) => (
                    <label key={type} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.useCase.includes(type)}
                        onCheckedChange={() => handleMultiSelectToggle("useCase", type)}
                        className="border-slate-300 data-[state=checked]:bg-[#FF6F40] data-[state=checked]:border-[#FF6F40]"
                      />
                      <span className="text-sm text-slate-600">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Market Selection */}
              <div>
                <Label className="text-sm font-semibold text-slate-700">Markets</Label>
                <div className="flex flex-wrap gap-3 mt-2">
                  {filterOptions.markets.map((market) => (
                    <label key={market} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.market.includes(market)}
                        onCheckedChange={() => handleMultiSelectToggle("market", market)}
                        className="border-slate-300 data-[state=checked]:bg-[#FF6F40] data-[state=checked]:border-[#FF6F40]"
                      />
                      <span className="text-sm text-slate-600">{market}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div>
                <Label className="text-sm font-semibold text-slate-700">Tags</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add a tag"
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddTag())}
                    data-testid="add-tag-input"
                  />
                  <Button type="button" onClick={handleAddTag} variant="outline" size="icon">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.tags.map((tag) => (
                      <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                        {tag}
                        <button type="button" onClick={() => handleRemoveTag(tag)} className="hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Pain Points */}
              <div>
                <Label className="text-sm font-semibold text-slate-700">Pain Points</Label>
                <div className="space-y-2 mt-1.5">
                  {formData.painPoints.map((point, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={point}
                        onChange={(e) => handleArrayFieldChange("painPoints", index, e.target.value)}
                        placeholder={`Pain point ${index + 1}`}
                      />
                      {formData.painPoints.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleArrayFieldRemove("painPoints", index)}
                          className="text-slate-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleArrayFieldAdd("painPoints")}
                    className="text-[#FF6F40] border-[#FF6F40] hover:bg-[#FFF1EC]"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Pain Point
                  </Button>
                </div>
              </div>

              {/* Solution */}
              <div>
                <Label htmlFor="solution" className="text-sm font-semibold text-slate-700">
                  Solution <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="solution"
                  value={formData.solution}
                  onChange={(e) => handleInputChange("solution", e.target.value)}
                  placeholder="Short paragraph describing the Arcus Power solution"
                  className={`mt-1.5 min-h-24 ${errors.solution ? "border-red-500" : ""}`}
                  data-testid="add-solution-input"
                />
                {errors.solution && <p className="text-red-500 text-xs mt-1">{errors.solution}</p>}
              </div>

              {/* Value Proposition */}
              <div>
                <Label className="text-sm font-semibold text-slate-700">Value Proposition</Label>
                <div className="space-y-2 mt-1.5">
                  {formData.valueProposition.map((value, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={value}
                        onChange={(e) => handleArrayFieldChange("valueProposition", index, e.target.value)}
                        placeholder={`Value proposition ${index + 1}`}
                      />
                      {formData.valueProposition.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleArrayFieldRemove("valueProposition", index)}
                          className="text-slate-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleArrayFieldAdd("valueProposition")}
                    className="text-[#FF6F40] border-[#FF6F40] hover:bg-[#FFF1EC]"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Value Proposition
                  </Button>
                </div>
              </div>

              {/* Technical Context */}
              <div>
                <Label htmlFor="technicalContext" className="text-sm font-semibold text-slate-700">
                  Technical Context (Optional)
                </Label>
                <Textarea
                  id="technicalContext"
                  value={formData.technicalContext}
                  onChange={(e) => handleInputChange("technicalContext", e.target.value)}
                  placeholder="Optional technical details about implementation"
                  className="mt-1.5 min-h-20"
                />
              </div>
            </div>
          </ScrollArea>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4">
            <div className="flex items-center justify-end gap-3">
              <Button type="button" variant="outline" onClick={onClose} data-testid="add-cancel-btn">
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#FF6F40] hover:bg-[#E65A2C] text-white font-semibold"
                data-testid="add-submit-btn"
              >
                Add Use Case
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUseCaseModal;
